"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDropMetadata } from "@/generated/models/ApiDropMetadata";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import isNumber from "lodash/isNumber";
import React, { useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import type { TraitsData } from "../memes/submission/types/TraitsData";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { FIELD_TO_LABEL_MAP, MEME_TRAITS_SORT_ORDER } from "../memes/traits/schema";
interface SingleWaveDropTraitsProps {
  readonly drop: ExtendedDrop;
}
interface SelectedTrait {
  readonly label: string;
  readonly displayValue: string;
}

// Component to display individual metadata items in cards
const MetadataItem: React.FC<{
  readonly label: string;
  readonly value: string | number | boolean;
  readonly onMobileSelect: (trait: SelectedTrait) => void;
}> = t0 => {
  const $ = _c(24);
  const {
    label,
    value,
    onMobileSelect
  } = t0;
  const isMobile = useIsMobileDevice();
  const displayValue = typeof value === "boolean" ? value ? "Yes" : "No" : String(value);
  const tooltipId = `trait-${label}-${displayValue}`;
  const valueClassName = isMobile ? "tw-text-xs tw-font-medium tw-leading-snug tw-text-iron-200 tw-line-clamp-2 tw-whitespace-normal tw-break-words" : "tw-text-xs tw-font-medium tw-text-iron-200 tw-truncate";
  let t1;
  if ($[0] !== label) {
    t1 = <span className="tw-mb-1 tw-block tw-text-[9px] tw-font-normal tw-uppercase tw-tracking-wide tw-text-iron-500">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== displayValue || $[3] !== tooltipId || $[4] !== valueClassName) {
    t2 = <span className={valueClassName} data-tooltip-id={tooltipId}>{displayValue}</span>;
    $[2] = displayValue;
    $[3] = tooltipId;
    $[4] = valueClassName;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== displayValue || $[7] !== isMobile || $[8] !== tooltipId) {
    t3 = !isMobile && <Tooltip id={tooltipId} place="top" offset={8} opacity={1} style={{
      ...TOOLTIP_STYLES,
      maxWidth: "300px",
      wordWrap: "break-word"
    }}><span className="tw-text-xs">{displayValue}</span></Tooltip>;
    $[6] = displayValue;
    $[7] = isMobile;
    $[8] = tooltipId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const content = t4;
  if (isMobile) {
    const t5 = `View full ${label} trait: ${displayValue}`;
    let t6;
    if ($[14] !== displayValue || $[15] !== label || $[16] !== onMobileSelect) {
      t6 = e => {
        e.stopPropagation();
        onMobileSelect({
          label,
          displayValue
        });
      };
      $[14] = displayValue;
      $[15] = label;
      $[16] = onMobileSelect;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== content || $[19] !== t5 || $[20] !== t6) {
      t7 = <button type="button" aria-label={t5} className="tw-flex-1 tw-bg-iron-900 tw-border tw-border-solid tw-border-iron-800 tw-rounded-lg tw-px-3 tw-py-2 tw-flex tw-flex-col tw-min-w-[100px] tw-cursor-pointer tw-appearance-none tw-text-left" onClick={t6}>{content}</button>;
      $[18] = content;
      $[19] = t5;
      $[20] = t6;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  let t5;
  if ($[22] !== content) {
    t5 = <div className="tw-flex-1 tw-bg-iron-900 tw-border tw-border-solid tw-border-iron-800 tw-rounded-lg tw-px-3 tw-py-2 tw-flex tw-flex-col tw-min-w-[100px]">{content}</div>;
    $[22] = content;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
};

// Extract trait data from drop metadata
const extractTraitsFromMetadata = (metadata: ApiDropMetadata[]): Partial<TraitsData> => {
  // Initialize with empty values for all fields to avoid type errors
  const traits: Partial<TraitsData> = {
    // Text fields
    artist: "",
    seizeArtistProfile: "",
    memeName: "",
    palette: "",
    style: "",
    jewel: "",
    superpower: "",
    dharma: "",
    gear: "",
    clothing: "",
    element: "",
    mystery: "",
    secrets: "",
    weapon: "",
    home: "",
    parent: "",
    sibling: "",
    food: "",
    drink: "",
    bonus: "",
    boost: "",
    title: "",
    description: "",
    // Boolean fields
    punk6529: false,
    gradient: false,
    movement: false,
    dynamic: false,
    interactive: false,
    collab: false,
    om: false,
    threeD: false,
    pepe: false,
    gm: false,
    summer: false,
    tulip: false,
    // Number fields
    pointsPower: 0,
    pointsWisdom: 0,
    pointsLoki: 0,
    pointsSpeed: 0
  };

  // Process each metadata item
  metadata.forEach(item => {
    // Normalize the key by removing spaces, dashes, and converting to lowercase
    const normalizedKey = item.data_key.replace(/[\s-_]+/g, "").replaceAll("points-", "points").replaceAll("type-", "type");
    const value = item.data_value;

    // Map for boolean traits (using normalized keys)
    const booleanTraits = ["punk6529", "gradient", "movement", "dynamic", "interactive", "collab", "om", "threed", "3d", "pepe", "gm", "summer", "tulip"];

    // Map for number traits (using normalized keys)
    const numberTraits = ["pointspower", "pointswisdom", "pointsloki", "pointsspeed"];

    // Comprehensive key mapping for string traits
    const keyMapping: Record<string, keyof TraitsData> = {
      // Basic info
      artist: "artist",
      artistname: "artist",
      memename: "memeName",
      meme: "memeName",
      title: "memeName",
      // Special properties
      palette: "palette",
      colorpalette: "palette",
      style: "style",
      artstyle: "style",
      jewel: "jewel",
      gem: "jewel",
      superpower: "superpower",
      power: "superpower",
      dharma: "dharma",
      gear: "gear",
      equipment: "gear",
      clothing: "clothing",
      outfit: "clothing",
      element: "element",
      bonus: "bonus",
      boost: "boost",
      // Additional details
      mystery: "mystery",
      secrets: "secrets",
      secret: "secrets",
      weapon: "weapon",
      home: "home",
      house: "home",
      parent: "parent",
      sibling: "sibling",
      food: "food",
      drink: "drink",
      beverage: "drink",
      // Special
      seizeartistprofile: "seizeArtistProfile",
      artistprofile: "seizeArtistProfile",
      profilehandle: "seizeArtistProfile"
    };

    // Process based on trait type
    if (booleanTraits.includes(normalizedKey)) {
      // Handle special case for "3d" which maps to "threeD"
      const traitKey = normalizedKey === "3d" ? "threeD" : normalizedKey;

      // Type guard: ensure we're only assigning to known boolean fields
      const booleanKeys: Array<keyof TraitsData> = ["punk6529", "gradient", "movement", "dynamic", "interactive", "collab", "om", "threeD", "pepe", "gm", "summer", "tulip"];
      if (booleanKeys.includes(traitKey as keyof TraitsData)) {
        const boolKey = traitKey as keyof TraitsData;
        const boolValue = value.toLowerCase() === "true" || value === "1" || value.toLowerCase() === "yes";

        // Safe assignment with type checking
        if (typeof traits[boolKey] === "boolean" || traits[boolKey] === undefined) {
          (traits as Record<keyof TraitsData, any>)[boolKey] = boolValue;
        }
      }
    } else if (numberTraits.includes(normalizedKey)) {
      // Map common variations to their canonical property names
      let traitKey: keyof TraitsData | null = null;

      // Type guard: map to known number fields
      if (["pointsPower", "pointsWisdom", "pointsLoki", "pointsSpeed"].includes(normalizedKey as keyof TraitsData)) {
        traitKey = normalizedKey as keyof TraitsData;
      }
      if (traitKey) {
        const numValue = Number(value) || 0;

        // Safe assignment with type checking
        if (typeof traits[traitKey] === "number" || traits[traitKey] === undefined) {
          (traits as Record<keyof TraitsData, any>)[traitKey] = numValue;
        }
      }
    } else if (keyMapping[normalizedKey.toLowerCase()]) {
      const textKey = keyMapping[normalizedKey.toLowerCase()];

      // Safe assignment with type checking
      if (typeof traits[textKey!] === "string" || traits[textKey!] === undefined) {
        (traits as Record<keyof TraitsData, any>)[textKey!] = value;
      }
    }
    // Special case: handle any key that starts with "points"
    else if (normalizedKey.startsWith("points")) {
      const pointType = normalizedKey.replace("points", "");
      const capitalizedPointType = pointType.charAt(0).toUpperCase() + pointType.slice(1);
      if (["Power", "Wisdom", "Loki", "Speed"].includes(capitalizedPointType)) {
        const camelCaseKey = `points${capitalizedPointType}` as keyof TraitsData;
        const pointValue = Number(value) || 0;

        // Safe assignment with type checking
        if (typeof traits[camelCaseKey] === "number" || traits[camelCaseKey] === undefined) {
          (traits as Record<keyof TraitsData, any>)[camelCaseKey] = pointValue;
        }
      }
    }
  });
  return traits;
};
export const SingleWaveDropTraits: React.FC<SingleWaveDropTraitsProps> = t0 => {
  const $ = _c(59);
  const {
    drop
  } = t0;
  const [showAllTraits, setShowAllTraits] = useState(false);
  const [selectedTrait, setSelectedTrait] = useState(null);
  let t1;
  if ($[0] !== drop.metadata) {
    t1 = extractTraitsFromMetadata(drop.metadata);
    $[0] = drop.metadata;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const extractedTraits = t1;
  const description = drop.parts && drop.parts.length > 0 ? drop.parts[0]?.content || "" : "";
  const t2 = drop.title || extractedTraits.title || "";
  const t3 = description || extractedTraits.description || "";
  const t4 = extractedTraits.artist ?? "";
  const t5 = extractedTraits.palette ?? "";
  const t6 = extractedTraits.style ?? "";
  const t7 = extractedTraits.jewel ?? "";
  const t8 = extractedTraits.superpower ?? "";
  const t9 = extractedTraits.dharma ?? "";
  const t10 = extractedTraits.gear ?? "";
  const t11 = extractedTraits.clothing ?? "";
  const t12 = extractedTraits.element ?? "";
  const t13 = extractedTraits.mystery ?? "";
  const t14 = extractedTraits.secrets ?? "";
  const t15 = extractedTraits.weapon ?? "";
  const t16 = extractedTraits.home ?? "";
  const t17 = extractedTraits.parent ?? "";
  const t18 = extractedTraits.sibling ?? "";
  const t19 = extractedTraits.food ?? "";
  const t20 = extractedTraits.drink ?? "";
  const t21 = extractedTraits.bonus ?? "";
  const t22 = extractedTraits.boost ?? "";
  const t23 = extractedTraits.punk6529 ?? false;
  const t24 = extractedTraits.gradient ?? false;
  const t25 = extractedTraits.movement ?? false;
  const t26 = extractedTraits.dynamic ?? false;
  const t27 = extractedTraits.interactive ?? false;
  const t28 = extractedTraits.collab ?? false;
  const t29 = extractedTraits.om ?? false;
  const t30 = extractedTraits.threeD ?? false;
  const t31 = extractedTraits.pepe ?? false;
  const t32 = extractedTraits.gm ?? false;
  const t33 = extractedTraits.summer ?? false;
  const t34 = extractedTraits.tulip ?? false;
  const t35 = extractedTraits.memeName ?? "";
  const t36 = extractedTraits.pointsPower ?? 0;
  const t37 = extractedTraits.pointsWisdom ?? 0;
  const t38 = extractedTraits.pointsLoki ?? 0;
  const t39 = extractedTraits.pointsSpeed ?? 0;
  const t40 = extractedTraits.seizeArtistProfile ?? "";
  let t41;
  if ($[2] !== t10 || $[3] !== t11 || $[4] !== t12 || $[5] !== t13 || $[6] !== t14 || $[7] !== t15 || $[8] !== t16 || $[9] !== t17 || $[10] !== t18 || $[11] !== t19 || $[12] !== t2 || $[13] !== t20 || $[14] !== t21 || $[15] !== t22 || $[16] !== t23 || $[17] !== t24 || $[18] !== t25 || $[19] !== t26 || $[20] !== t27 || $[21] !== t28 || $[22] !== t29 || $[23] !== t3 || $[24] !== t30 || $[25] !== t31 || $[26] !== t32 || $[27] !== t33 || $[28] !== t34 || $[29] !== t35 || $[30] !== t36 || $[31] !== t37 || $[32] !== t38 || $[33] !== t39 || $[34] !== t4 || $[35] !== t40 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t41 = {
      title: t2,
      description: t3,
      artist: t4,
      palette: t5,
      style: t6,
      jewel: t7,
      superpower: t8,
      dharma: t9,
      gear: t10,
      clothing: t11,
      element: t12,
      mystery: t13,
      secrets: t14,
      weapon: t15,
      home: t16,
      parent: t17,
      sibling: t18,
      food: t19,
      drink: t20,
      bonus: t21,
      boost: t22,
      punk6529: t23,
      gradient: t24,
      movement: t25,
      dynamic: t26,
      interactive: t27,
      collab: t28,
      om: t29,
      threeD: t30,
      pepe: t31,
      gm: t32,
      summer: t33,
      tulip: t34,
      memeName: t35,
      pointsPower: t36,
      pointsWisdom: t37,
      pointsLoki: t38,
      pointsSpeed: t39,
      seizeArtistProfile: t40
    };
    $[2] = t10;
    $[3] = t11;
    $[4] = t12;
    $[5] = t13;
    $[6] = t14;
    $[7] = t15;
    $[8] = t16;
    $[9] = t17;
    $[10] = t18;
    $[11] = t19;
    $[12] = t2;
    $[13] = t20;
    $[14] = t21;
    $[15] = t22;
    $[16] = t23;
    $[17] = t24;
    $[18] = t25;
    $[19] = t26;
    $[20] = t27;
    $[21] = t28;
    $[22] = t29;
    $[23] = t3;
    $[24] = t30;
    $[25] = t31;
    $[26] = t32;
    $[27] = t33;
    $[28] = t34;
    $[29] = t35;
    $[30] = t36;
    $[31] = t37;
    $[32] = t38;
    $[33] = t39;
    $[34] = t4;
    $[35] = t40;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t41;
  } else {
    t41 = $[41];
  }
  const finalTraits = t41;
  const traits = finalTraits;
  let items;
  if ($[42] !== traits) {
    const priorityKeys = ["artist", "seizeArtistProfile", "memeName", "title", "description", "punk6529", "gradient", "movement", "dynamic", "interactive", "collab", "om", "threeD", "pepe", "gm", "summer", "tulip", "style", "palette", "jewel", "superpower", "dharma", "element", "gear", "clothing", "weapon", "home", "parent", "sibling", "food", "drink", "mystery", "secrets", "bonus", "boost", "pointsPower", "pointsWisdom", "pointsLoki", "pointsSpeed"];
    items = [];
    priorityKeys.forEach(key => {
      if (key in traits && traits[key as keyof TraitsData] !== undefined) {
        const value = traits[key as keyof TraitsData];
        if (typeof value === "string" && value || typeof value === "number" || typeof value === "boolean") {
          items.push({
            label: key,
            value
          });
        }
      }
    });
    Object.entries(traits).forEach(t42 => {
      const [key_0, value_0] = t42;
      if (!priorityKeys.includes(key_0)) {
        if (typeof value_0 === "string" && value_0 || typeof value_0 === "number" || typeof value_0 === "boolean") {
          items.push({
            label: key_0,
            value: value_0
          });
        }
      }
    });
    items.sort(_temp);
    items.forEach(_temp3);
    $[42] = traits;
    $[43] = items;
  } else {
    items = $[43];
  }
  const traitItems = items;
  if (traitItems.length === 0) {
    return null;
  }
  let t42;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = e => {
      e.stopPropagation();
      setShowAllTraits(true);
    };
    $[44] = t42;
  } else {
    t42 = $[44];
  }
  const handleShowMore = t42;
  let t43;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = e_0 => {
      e_0.stopPropagation();
      setShowAllTraits(false);
    };
    $[45] = t43;
  } else {
    t43 = $[45];
  }
  const handleShowLess = t43;
  let t44;
  if ($[46] !== traitItems) {
    t44 = traitItems.slice(0, 3).map(item_0 => <MetadataItem key={item_0.label} label={FIELD_TO_LABEL_MAP[item_0.label as keyof typeof FIELD_TO_LABEL_MAP] ?? item_0.label} value={item_0.value} onMobileSelect={setSelectedTrait} />);
    $[46] = traitItems;
    $[47] = t44;
  } else {
    t44 = $[47];
  }
  let t45;
  if ($[48] !== showAllTraits || $[49] !== traitItems) {
    t45 = traitItems.length > 3 && (showAllTraits ? <>{traitItems.slice(3).map(item_1 => <MetadataItem key={item_1.label} label={FIELD_TO_LABEL_MAP[item_1.label as keyof typeof FIELD_TO_LABEL_MAP] ?? item_1.label} value={item_1.value} onMobileSelect={setSelectedTrait} />)}<button onClick={handleShowLess} className="hover:tw-text-iron-40 tw-flex tw-min-w-[100px] tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/30 tw-px-3 tw-py-2 tw-text-xs tw-text-iron-300 tw-transition-colors">Show less</button></> : <button onClick={handleShowMore} className="tw-flex tw-min-w-[100px] tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/30 tw-px-3 tw-py-2 tw-text-xs tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50">Show all</button>);
    $[48] = showAllTraits;
    $[49] = traitItems;
    $[50] = t45;
  } else {
    t45 = $[50];
  }
  let t46;
  if ($[51] !== t44 || $[52] !== t45) {
    t46 = <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 lg:tw-grid-cols-4">{t44}{t45}</div>;
    $[51] = t44;
    $[52] = t45;
    $[53] = t46;
  } else {
    t46 = $[53];
  }
  let t47;
  if ($[54] !== selectedTrait) {
    t47 = selectedTrait && <MobileWrapperDialog title={selectedTrait.label} isOpen={true} onClose={() => setSelectedTrait(null)}><div className="tw-px-4 sm:tw-px-6"><p className="tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-text-sm tw-leading-relaxed tw-text-iron-100">{selectedTrait.displayValue}</p></div></MobileWrapperDialog>;
    $[54] = selectedTrait;
    $[55] = t47;
  } else {
    t47 = $[55];
  }
  let t48;
  if ($[56] !== t46 || $[57] !== t47) {
    t48 = <div className="tw-w-full">{t46}{t47}</div>;
    $[56] = t46;
    $[57] = t47;
    $[58] = t48;
  } else {
    t48 = $[58];
  }
  return t48;
};
function _temp(a, b) {
  const aIndex = MEME_TRAITS_SORT_ORDER.indexOf(a.label);
  const bIndex = MEME_TRAITS_SORT_ORDER.indexOf(b.label);
  return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
}
function _temp2(str) {
  return str.toUpperCase();
}
function _temp3(item) {
  const knownKey = FIELD_TO_LABEL_MAP[item.label as keyof typeof FIELD_TO_LABEL_MAP];
  const formattedKey = knownKey ?? item.label.replace(/([A-Z])/g, " $1").replace(/^./, _temp2).trim();
  item.label = formattedKey;
  if (isNumber(item.value)) {
    item.value = item.value.toLocaleString();
  }
}